import classnames from 'classnames';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { isInHomePage } from '../../shared/functions/msdyn-helpers';

import { Drawer } from '../../shared/components/drawer/drawer';
import { useIsMobile } from '../../shared/hooks/useIsMobile';
import { useIdentityDatalayer } from '../../integrations/datalayer/hooks/useIdentityDatalayer';
import { useConsentCookieSync } from '../../shared/hooks/useConsentCookieSync';
import { useUserUpdateTracker } from '../../integrations/relewise/hooks/useUserUpdateTracker';
import { CusHeaderLogo } from './components/cus-header-logo';
import GenderRedirect from './components/gender-redirect';
import { useDataLayerTelemetry } from './hooks/use-datalayer-telemetry';
import { useGenderHeader } from './hooks/use-gender-header';
import { useLocaleRedirect } from './hooks/use-locale-redirect';
import { useLoyaltyCardSync } from './hooks/use-loyalty-card-sync';
import { useNewsletterConsentSync } from './hooks/use-newsletter-consent-sync';
import { ICusHeaderData } from './cus-header.data';
import { ICusHeaderProps, layout } from './cus-header.props.autogenerated';

const mobileMenuId = 'cus-header-nav-mobile';

const CusHeader = (props: ICusHeaderProps<ICusHeaderData>): JSX.Element | null => {
    const { config, context, data, resources, slots } = props;

    const isMobile = useIsMobile(context);
    const isLogoOnlyCentered = config.layout === layout.logoOnlyCentered;
    const isInFlow = !isInHomePage(context);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    useConsentCookieSync(context.request);

    useGenderHeader({ context });

    useLocaleRedirect({
        request: context.request,
        telemetry: props.telemetry,
        shouldRedirectToLocale: config.shouldRedirectToLocale,
        deleteCookiesOnLocaleSwitch: config.deleteCookiesOnLocaleSwitch,
        shouldLogOutInvalidLocaleUser: config.shouldLogOutInvalidLocaleUser,
        cookieExpiration: context.request.app.config.cookieExpiration
    });

    useNewsletterConsentSync({
        enabled: config.enableNewsletterConsentSync,
        context,
        customer: data.customerInformation?.result,
        customerStatus: data.customerInformation?.status
    });

    useLoyaltyCardSync({
        context,
        customer: data.customerInformation?.result,
        customerStatus: data.customerInformation?.status
    });

    useDataLayerTelemetry({ telemetry: context.telemetry });

    useIdentityDatalayer({
        request: context.request,
        telemetry: props.telemetry,
        customer: data.customerInformation?.result,
        isSignInPage: config.isSignInPage
    });

    useUserUpdateTracker(context.request, props.telemetry);

    const toggleDrawer = useCallback(() => {
        setIsDrawerOpen(open => !open);
    }, []);

    const closeDrawer = useCallback(() => {
        setIsDrawerOpen(false);
    }, []);

    useEffect(() => {
        if (!isMobile) {
            setIsDrawerOpen(false);
        }
    }, [isMobile]);

    const logo = (
        <CusHeaderLogo
            className='cus-header__logo'
            image={config.logoImage}
            link={config.logoLink}
            requestContext={context.request}
            gridSettings={context.request.gridSettings ?? {}}
        />
    );

    const navigation = !isLogoOnlyCentered && slots?.menuBar?.length ? slots.menuBar : undefined;

    const search = !isLogoOnlyCentered && slots?.search?.length ? <div className='cus-header__search'>{slots.search}</div> : undefined;

    const wishlistIcon =
        !isLogoOnlyCentered && slots?.wishlistIcon?.length ? <div className='cus-header__wishlist'>{slots.wishlistIcon}</div> : undefined;

    const accountIcon =
        !isLogoOnlyCentered && slots?.accountIcon?.length ? <div className='cus-header__account'>{slots.accountIcon}</div> : undefined;

    const cartIcon = !isLogoOnlyCentered && slots?.cartIcon?.length ? <div className='cus-header__cart'>{slots.cartIcon}</div> : undefined;

    const navIcon = navigation && (
        <button
            type='button'
            className='cus-header__nav-icon'
            onClick={toggleDrawer}
            title={resources.mobileHamburgerAriaLabel}
            aria-label={resources.mobileHamburgerAriaLabel}
            aria-expanded={isDrawerOpen}
            aria-controls={mobileMenuId}
        />
    );

    const mobileMenu = navigation && (
        <Drawer
            className='cus-header__drawer'
            isOpen={isDrawerOpen}
            onClose={closeDrawer}
            title={resources.mobileMenuTitle}
            closeButtonLabel={resources.mobileMenuCloseButtonLabel}
        >
            <div id={mobileMenuId} className='cus-header__drawer-nav'>
                {navigation}
            </div>
        </Drawer>
    );

    return (
        <>
            {isInFlow && !isLogoOnlyCentered && <div className='cus-header__spacer' aria-hidden='true' />}
            <header
                className={classnames('cus-header', {
                    'cus-header--mobile': isMobile,
                    'cus-header--logo-only-centered': isLogoOnlyCentered,
                    'cus-header--in-flow': isInFlow
                })}
                aria-label={resources.headerAriaLabel}
            >
                <GenderRedirect context={context} enableRedirectOnCookie={config.enableRedirectOnCookie} />
                <div className='cus-header__container'>
                    {logo}
                    {navigation && <div className='cus-header__navigation'>{navigation}</div>}
                    {!isLogoOnlyCentered && (
                        <div className='cus-header__actions'>
                            {search}
                            {wishlistIcon}
                            {accountIcon}
                            {cartIcon}
                            {navIcon}
                        </div>
                    )}
                </div>
                {mobileMenu}
            </header>
        </>
    );
};

export default CusHeader;
